import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Card, Header, Icon, Segment, Grid, Dropdown, Form, Button, Image } from "semantic-ui-react";
import { Badge, Timeline } from "../../common";
import moment from "moment";
import * as leaveApprovalActions from "./leaveApprovalActions";
import { getUserInfo } from "../../../api/axios";
import "./ApprovalHistory.css";

class ApprovalHistory extends Component {
    constructor(props) {
        super(props);
        this.state = {
            historyType: "leave", // leave, group, approver
            selectedId: "",
            searchText: "",
            dateRange: "all", // all, week, month, quarter, year
        };
    }

    componentDidMount() {
        this.loadHistory();
    }

    loadHistory = () => {
        const { historyType, selectedId } = this.state;
        const userInfo = getUserInfo();

        if (!selectedId && historyType === "approver") {
            this.setState({ selectedId: userInfo?.id || "" });
        }

        if (selectedId) {
            switch (historyType) {
                case "leave":
                    this.props.leaveApprovalActions.getApprovalHistoryByLeaveId(selectedId);
                    break;
                case "group":
                    this.props.leaveApprovalActions.getApprovalHistoryByGroup(selectedId);
                    break;
                case "approver":
                    this.props.leaveApprovalActions.getApprovalHistoryByApprover(selectedId);
                    break;
                default:
                    break;
            }
        }
    };

    handleHistoryTypeChange = (e, { value }) => {
        this.setState({ historyType: value, selectedId: "" }, () => {
            if (value === "approver") {
                const userInfo = getUserInfo();
                this.setState({ selectedId: userInfo?.id || "" }, this.loadHistory);
            }
        });
    };

    handleIdChange = (e, { value }) => {
        this.setState({ selectedId: value }, this.loadHistory);
    };

    handleSearchChange = (e, { value }) => {
        this.setState({ searchText: value });
    };

    handleDateRangeChange = (e, { value }) => {
        this.setState({ dateRange: value });
    };

    getStatusIcon = (status) => {
        const statusConfig = {
            0: { name: "clock", color: "yellow" },
            1: { name: "check circle", color: "green" },
            2: { name: "times circle", color: "red" },
        };
        return statusConfig[status] || statusConfig[0];
    };

    getStatusBadge = (status) => {
        const statusConfig = {
            0: { color: "yellow", text: "Pending" },
            1: { color: "green", text: "Approved" },
            2: { color: "red", text: "Rejected" },
        };
        const config = statusConfig[status] || statusConfig[0];
        return (
            <Badge color={config.color} size="small">
                {config.text}
            </Badge>
        );
    };

    getFilteredHistory = () => {
        const { approvalHistory } = this.props.leaveApprovalReducer;
        const { searchText, dateRange } = this.state;

        let filtered = approvalHistory;

        // Filter by search text
        if (searchText) {
            filtered = filtered.filter(
                (item) =>
                    item.employeeName?.toLowerCase().includes(searchText.toLowerCase()) ||
                    item.leaveTypeName?.toLowerCase().includes(searchText.toLowerCase()) ||
                    item.approverName?.toLowerCase().includes(searchText.toLowerCase()) ||
                    item.comments?.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        // Filter by date range
        if (dateRange !== "all") {
            const now = moment();
            let startDate;

            switch (dateRange) {
                case "week":
                    startDate = now.clone().subtract(1, "week");
                    break;
                case "month":
                    startDate = now.clone().subtract(1, "month");
                    break;
                case "quarter":
                    startDate = now.clone().subtract(3, "months");
                    break;
                case "year":
                    startDate = now.clone().subtract(1, "year");
                    break;
                default:
                    startDate = null;
            }

            if (startDate) {
                filtered = filtered.filter((item) =>
                    moment(item.createdDate).isAfter(startDate)
                );
            }
        }

        return filtered.sort((a, b) => moment(b.createdDate) - moment(a.createdDate));
    };

    renderTimelineItem = (item) => {
        const statusIcon = this.getStatusIcon(item.approvalStatus);
        const isStatusChange = item.previousStatus && item.newStatus;

        return (
            <Timeline.Item key={item.id}>
                <div className="timeline-content">
                    <Card className="history-card">
                        <Card.Content>
                            <div className="history-header">
                                <div className="user-info">
                                    <Image
                                        src={item.approverImageUrl || "/assets/img/user-placeholder.png"}
                                        size="mini"
                                        circular
                                        className="approver-avatar"
                                    />
                                    <div>
                                        <Header as="h5" className="approver-name">
                                            {item.approverName}
                                        </Header>
                                        <p className="approver-email">{item.approverEmail}</p>
                                    </div>
                                </div>
                                <div className="status-info">
                                    {this.getStatusBadge(item.approvalStatus)}
                                    <div className="approval-date">
                                        {moment(item.approvalDate || item.createdDate).format("MMM DD, YYYY HH:mm")}
                                    </div>
                                </div>
                            </div>

                            <div className="leave-details">
                                <div className="employee-info">
                                    <strong>Employee:</strong> {item.employeeName}
                                </div>
                                <div className="leave-type">
                                    <strong>Leave Type:</strong> {item.leaveTypeName}
                                </div>
                                <div className="leave-dates">
                                    <strong>Date:</strong> {moment(item.createdDate).format("MMM DD, YYYY")}
                                </div>
                            </div>

                            {isStatusChange && (
                                <div className="status-change">
                                    <Icon name="arrow right" />
                                    <span>
                                        Status changed from <strong>{item.previousStatus}</strong> to{" "}
                                        <strong>{item.newStatus}</strong>
                                    </span>
                                </div>
                            )}

                            {item.comments && (
                                <div className="comments">
                                    <Icon name="comment" />
                                    <span>{item.comments}</span>
                                </div>
                            )}

                            <div className="meta-info">
                                <small>
                                    <Icon name="globe" />
                                    IP: {item.ipAddress || "N/A"}
                                </small>
                                <small>
                                    <Icon name="desktop" />
                                    {item.userAgent ? item.userAgent.substring(0, 50) + "..." : "N/A"}
                                </small>
                            </div>
                        </Card.Content>
                    </Card>
                </div>
            </Timeline.Item>
        );
    };

    render() {
        const { leaveApprovalReducer } = this.props;
        const { loading, error, approvalHistory } = leaveApprovalReducer;
        const { historyType, selectedId, searchText, dateRange } = this.state;

        const filteredHistory = this.getFilteredHistory();

        const historyTypeOptions = [
            { key: "leave", text: "By Leave ID", value: "leave" },
            { key: "group", text: "By Group", value: "group" },
            { key: "approver", text: "By Approver", value: "approver" },
        ];

        const dateRangeOptions = [
            { key: "all", text: "All Time", value: "all" },
            { key: "week", text: "Last Week", value: "week" },
            { key: "month", text: "Last Month", value: "month" },
            { key: "quarter", text: "Last Quarter", value: "quarter" },
            { key: "year", text: "Last Year", value: "year" },
        ];

        return (
            <div className="approval-history">
                <Header as="h2" className="history-header">
                    <Icon name="history" />
                    Approval History
                    <Header.Subheader>
                        Track the complete audit trail of leave approvals
                    </Header.Subheader>
                </Header>

                {/* Filters */}
                <Segment className="filters-section">
                    <Grid columns={4} stackable>
                        <Grid.Column>
                            <Form.Field>
                                <label>History Type</label>
                                <Dropdown
                                    selection
                                    options={historyTypeOptions}
                                    value={historyType}
                                    onChange={this.handleHistoryTypeChange}
                                    placeholder="Select history type"
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>
                                    {historyType === "leave" ? "Leave ID" :
                                        historyType === "group" ? "Group" : "Approver ID"}
                                </label>
                                <Form.Input
                                    placeholder={`Enter ${historyType === "leave" ? "Leave ID" :
                                        historyType === "group" ? "Group" : "Approver ID"}`}
                                    value={selectedId}
                                    onChange={(e) => this.setState({ selectedId: e.target.value })}
                                    onKeyPress={(e) => e.key === "Enter" && this.loadHistory()}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Search</label>
                                <Form.Input
                                    icon="search"
                                    placeholder="Search in history..."
                                    value={searchText}
                                    onChange={this.handleSearchChange}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Date Range</label>
                                <Dropdown
                                    selection
                                    options={dateRangeOptions}
                                    value={dateRange}
                                    onChange={this.handleDateRangeChange}
                                    placeholder="Select date range"
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid>
                    <div className="filter-actions">
                        <Button primary onClick={this.loadHistory} loading={loading}>
                            <Icon name="refresh" />
                            Load History
                        </Button>
                    </div>
                </Segment>

                {/* History Timeline */}
                {loading ? (
                    <Segment loading>
                        <div style={{ height: "200px" }}></div>
                    </Segment>
                ) : error ? (
                    <Segment color="red">
                        <Header as="h4" color="red">
                            <Icon name="warning" />
                            Error
                        </Header>
                        <p>{error}</p>
                    </Segment>
                ) : !selectedId ? (
                    <Segment textAlign="center" className="empty-state">
                        <Icon name="search" size="huge" color="grey" />
                        <Header as="h3" color="grey">
                            Select criteria to view history
                        </Header>
                        <p>Please select a history type and enter the required ID to view approval history.</p>
                    </Segment>
                ) : filteredHistory.length === 0 ? (
                    <Segment textAlign="center" className="empty-state">
                        <Icon name="history" size="huge" color="grey" />
                        <Header as="h3" color="grey">
                            No history found
                        </Header>
                        <p>No approval history matches your current criteria.</p>
                    </Segment>
                ) : (
                    <div className="history-timeline">
                        <Timeline>
                            {filteredHistory.map(this.renderTimelineItem)}
                        </Timeline>
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    leaveApprovalReducer: state.leaveApprovalReducer,
});

const mapDispatchToProps = (dispatch) => ({
    leaveApprovalActions: bindActionCreators(leaveApprovalActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ApprovalHistory); 